import axiosInstance from '../../axiosInstance';

interface ZohoAsapWidgetOptions {
  name?: string;
  id?: string;
  myAppPortalId?: string;
  privacySettings?: {
    web?: { clientId?: string | null };
  };
  whiteListedDomains?: string[] | null;
}

type ZohoDeskAsapGlobal = {
  invoke: (method: string, ...args: unknown[]) => void;
  set: (key: string, value: unknown) => void;
  _defaultoptions?: ZohoAsapWidgetOptions;
};

declare global {
  interface Window {
    ZohoDeskAsap?: ZohoDeskAsapGlobal;
    ZohoDeskAsapReady?: (callback: () => void) => void;
    ZohoDeskAsapReadyStatus?: boolean;
    ZohoDeskAsap__asyncalls?: Array<() => void>;
  }
}

const appId = process.env.REACT_APP_ZOHO_APP_ID;
const orgId = process.env.REACT_APP_ZOHO_ORG_ID;
const expectedClientId = process.env.REACT_APP_ZOHO_CLIENT_ID?.trim();
const ASAP_READY_TIMEOUT_MS = 30_000;

export const ASAP_AUTHENTICATED_BODY_CLASS = 'asap-authenticated';

const ZOHO_ASAP_DOM_SELECTORS = [
  '#zohodeskasapscript',
  '[id^="zohoasap"]',
  '[id^="zoho-hc-asap"]',
  '[class*="zohoasap"]',
  '[class*="ZohoAsap"]',
  'iframe[src*="desk.zoho.com"]',
  'iframe[src*="zoho.com/portal"]',
  'iframe[src*="zohodesk"]',
];

let sessionCleanupPromise: Promise<void> | null = null;
let tokenRefreshTimerId: number | null = null;

/** Refresh this long before JWT exp (must be less than backend TTL). */
const TOKEN_REFRESH_BUFFER_MS = 15_000;

export function parseAllowedRoles(raw: string | undefined): string[] {
  const value =
    raw?.trim() || 'Dentist,Dental Assistant';
  return value
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);
}

export function isZohoAsapConfigured(): boolean {
  return Boolean(appId && orgId);
}

export function getAsapScriptUrl(): string | null {
  if (!isZohoAsapConfigured()) {
    return null;
  }

  return `https://desk.zoho.com/portal/api/web/asapApp/${appId}?orgId=${orgId}`;
}

function getLoadedWidgetOptions(): ZohoAsapWidgetOptions | null {
  return window.ZohoDeskAsap?._defaultoptions ?? null;
}

export function getLoadedWidgetClientId(): string | null {
  return window.ZohoDeskAsap?._defaultoptions?.privacySettings?.web?.clientId ?? null;
}

function validateLoadedWidgetConfig(): void {
  const options = getLoadedWidgetOptions();
  if (!options) {
    return;
  }

  const loadedClientId = options.privacySettings?.web?.clientId ?? '';

  if (
    expectedClientId &&
    loadedClientId &&
    loadedClientId !== expectedClientId
  ) {
    console.error(
      [
        'Zoho ASAP widget client_id mismatch.',
        `Loaded "${loadedClientId}" from app ${options.id ?? appId}.`,
        'Update REACT_APP_ZOHO_APP_ID and REACT_APP_ZOHO_ORG_ID from the correct widget Get Code snippet in Zoho Desk,',
        'and copy ZOHO_ASAP_SECRET from that same widget JWT settings.',
      ].join(' '),
    );
  }

  if (!options.whiteListedDomains?.length) {
    console.error(
      [
        `Zoho ASAP widget "${options.name ?? options.id ?? appId}" has no whitelisted domains (whiteListedDomains is empty).`,
        'JWT remote/auth will return general_error until you add your portal/ngrok host in',
        'Zoho Desk → ASAP → Secure your help widget → JWT Authentication → Allowed domains.',
      ].join(' '),
    );
  }

  if (process.env.NODE_ENV === 'development') {
    console.info('Zoho ASAP widget loaded', {
      widgetName: options.name,
      widgetId: options.id,
      portalId: options.myAppPortalId,
      clientId: loadedClientId,
      scriptUrl: getAsapScriptUrl(),
    });
  }
}

export function loadScript(): void {
  if (!isZohoAsapConfigured()) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'Zoho ASAP not loaded: set REACT_APP_ZOHO_APP_ID and REACT_APP_ZOHO_ORG_ID',
      );
    }
    return;
  }

  if (document.getElementById('zohodeskasapscript')) {
    return;
  }

  const scriptUrl = getAsapScriptUrl()!;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'zohodeskasapscript';
  script.defer = true;
  script.src = scriptUrl;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  window.ZohoDeskAsapReady = function (callback) {
    const asyncCalls =
      (window.ZohoDeskAsap__asyncalls = window.ZohoDeskAsap__asyncalls || []);

    if (window.ZohoDeskAsapReadyStatus) {
      if (callback) asyncCalls.push(callback);
      asyncCalls.forEach((cb) => cb && cb());
      window.ZohoDeskAsap__asyncalls = undefined;
    } else if (callback) {
      asyncCalls.push(callback);
    }
  };
}

export function ensureReady(): Promise<void> {
  loadScript();

  return new Promise((resolve, reject) => {
    if (typeof window.ZohoDeskAsapReady !== 'function') {
      reject(new Error('ZohoDeskAsapReady hook failed to initialize'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      reject(new Error('Timed out waiting for Zoho ASAP widget'));
    }, ASAP_READY_TIMEOUT_MS);

    window.ZohoDeskAsapReady(() => {
      window.clearTimeout(timeoutId);
      validateLoadedWidgetConfig();
      resolve();
    });
  });
}

export type ZohoLoginCallback = (
  success: (token: string) => void,
  failure: (error: unknown) => void,
) => void;

function normalizeAsapError(error: unknown, fallback: string): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'object' && error && 'errMsg' in error) {
    return new Error(String((error as { errMsg: unknown }).errMsg));
  }

  return new Error(fallback);
}

function safeInvokeAsap(method: string, ...args: unknown[]): void {
  void safeInvokeAsapAsync(method, ...args);
}

function safeInvokeAsapAsync(
  method: string,
  ...args: unknown[]
): Promise<void> {
  if (!window.ZohoDeskAsap?.invoke) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    try {
      const result = window.ZohoDeskAsap!.invoke(method, ...args) as unknown;
      if (
        result &&
        typeof result === 'object' &&
        'then' in result &&
        typeof (result as Promise<unknown>).then === 'function'
      ) {
        void (result as Promise<unknown>)
          .then(() => resolve())
          .catch(() => resolve());
        return;
      }
      resolve();
    } catch {
      resolve();
    }
  });
}

function clearZohoAsapPrefill(): void {
  if (typeof window.ZohoDeskAsap?.set !== 'function') {
    return;
  }

  try {
    window.ZohoDeskAsap.set('ticket.form.prefillValues', {});
  } catch {
    // Widget may already be torn down.
  }
}

function removeZohoAsapDom(): void {
  for (const selector of ZOHO_ASAP_DOM_SELECTORS) {
    document.querySelectorAll(selector).forEach((element) => {
      element.remove();
    });
  }
}

function clearZohoWindowGlobals(): void {
  delete window.ZohoDeskAsap;
  delete window.ZohoDeskAsapReady;
  delete window.ZohoDeskAsapReadyStatus;
  delete window.ZohoDeskAsap__asyncalls;
}

function prepareAsapForLogin(): void {
  safeInvokeAsap('logout');
  safeInvokeAsap('hide');
  safeInvokeAsap('hide', 'app.launcher');
}

function refreshAsapUiAfterLogin(): void {
  // ASAP v1 helpers used by PoC; no-op safely on ASAP 2.0.
  safeInvokeAsap('reload');
  safeInvokeAsap('show');
  safeInvokeAsap('show', 'app.launcher');
}

function decodeJwtExpMs(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) {
      return null;
    }
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized)) as { exp?: number };
    if (typeof payload.exp !== 'number') {
      return null;
    }
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

export function stopZohoAsapTokenRefresh(): void {
  if (tokenRefreshTimerId !== null) {
    window.clearTimeout(tokenRefreshTimerId);
    tokenRefreshTimerId = null;
  }
}

function scheduleZohoAsapTokenRefresh(
  getAccessToken: () => string | null | undefined,
  zohoJwt: string,
): void {
  stopZohoAsapTokenRefresh();

  const expMs = decodeJwtExpMs(zohoJwt);
  if (!expMs) {
    return;
  }

  const delay = Math.max(expMs - Date.now() - TOKEN_REFRESH_BUFFER_MS, 5_000);

  if (process.env.NODE_ENV === 'development') {
    console.info('[Zoho ASAP] Next JWT refresh scheduled', {
      inSeconds: Math.round(delay / 1000),
      expiresAt: new Date(expMs).toISOString(),
    });
  }

  tokenRefreshTimerId = window.setTimeout(() => {
    tokenRefreshTimerId = null;
    if (process.env.NODE_ENV === 'development') {
      console.info(
        '[Zoho ASAP] JWT nearing expiry — refetching via GET /api/zoho-asap/token',
      );
    }

    void invokeLogin(getAccessToken).catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Zoho ASAP] JWT refresh failed', error);
      }
    });
  }, delay);
}

export function createLoginCallback(
  getAccessToken: () => string | null | undefined,
  onAuthenticated?: () => void,
  onFailed?: (error: unknown) => void,
): ZohoLoginCallback {
  return (success, failure) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      const error = new Error('No active session');
      failure(error);
      onFailed?.(error);
      return;
    }

    axiosInstance
      .get<{ token: string }>('zoho-asap/token', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => {
        const token = response.data?.token;
        if (!token) {
          throw new Error('Token missing from response');
        }
        success(token);
        refreshAsapUiAfterLogin();
        scheduleZohoAsapTokenRefresh(getAccessToken, token);
        onAuthenticated?.();
      })
      .catch((error) => {
        const normalized = normalizeAsapError(
          error,
          'ASAP token request failed',
        );
        failure(normalized);
        onFailed?.(normalized);
      });
  };
}

export interface TicketPrefillOptions {
  userId: string;
  practiceId?: string;
}

export function isTicketPrefillEnabled(): boolean {
  return process.env.REACT_APP_ZOHO_TICKET_PREFILL_ENABLED !== 'false';
}

export function getPracticeIdFieldApiName(): string | null {
  const value = process.env.REACT_APP_ZOHO_TICKET_PRACTICE_ID_FIELD?.trim();
  return value || null;
}

function applyPrefillValues(prefillValues: Record<string, unknown>): boolean {
  if (typeof window.ZohoDeskAsap?.set !== 'function') {
    return false;
  }

  window.ZohoDeskAsap.set('ticket.form.prefillValues', prefillValues);
  return true;
}

export function applyTicketPrefill({
  userId,
  practiceId,
}: TicketPrefillOptions): boolean {
  if (!isTicketPrefillEnabled()) {
    return false;
  }

  const departmentId = process.env.REACT_APP_ZOHO_TICKET_DEPARTMENT_ID?.trim();
  const layoutId = process.env.REACT_APP_ZOHO_TICKET_LAYOUT_ID?.trim();
  const userIdField =
    process.env.REACT_APP_ZOHO_TICKET_USER_ID_FIELD?.trim() || 'cf_user_id';
  const practiceIdField = getPracticeIdFieldApiName();

  if (!departmentId || !layoutId || !userId) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'Zoho ticket prefill skipped: departmentId, layoutId, or userId missing',
      );
    }
    return false;
  }

  const layoutKey = `${departmentId}&&&${layoutId}`;
  const fields: Record<string, { defaultValue: string; isHidden: boolean }> = {
    departmentId: {
      defaultValue: departmentId,
      isHidden: true,
    },
    layoutId: {
      defaultValue: layoutId,
      isHidden: true,
    },
    [userIdField]: {
      defaultValue: String(userId),
      isHidden: true,
    },
  };

  if (practiceIdField && practiceId) {
    fields[practiceIdField] = {
      defaultValue: String(practiceId),
      isHidden: true,
    };
  }

  try {
    const prefillValues = { [layoutKey]: fields };
    if (typeof window.ZohoDeskAsapReady === 'function') {
      window.ZohoDeskAsapReady(() => {
        applyPrefillValues(prefillValues);
      });
      return true;
    }

    return applyPrefillValues(prefillValues);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Zoho ticket prefill failed', error);
    }
    return false;
  }
}

export function invokeLogin(
  getAccessToken: () => string | null | undefined,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!window.ZohoDeskAsap?.invoke) {
      reject(new Error('Zoho Desk ASAP widget is not loaded'));
      return;
    }

    const runLogin = () => {
      try {
        prepareAsapForLogin();

        window.ZohoDeskAsap.invoke(
          'login',
          createLoginCallback(getAccessToken, resolve, reject),
        );
      } catch (error) {
        reject(
          normalizeAsapError(error, 'ASAP login failed'),
        );
      }
    };

    if (typeof window.ZohoDeskAsapReady === 'function') {
      window.ZohoDeskAsapReady(runLogin);
      return;
    }

    runLogin();
  });
}

export async function invokeLogout(): Promise<void> {
  clearZohoAsapPrefill();
  const logoutSteps = [
    safeInvokeAsapAsync('close'),
    safeInvokeAsapAsync('logout'),
    safeInvokeAsapAsync('hide', 'app.launcher'),
    safeInvokeAsapAsync('hide'),
  ];

  await Promise.race([
    Promise.all(logoutSteps),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 2_000);
    }),
  ]);
}

/**
 * Ends the Zoho ASAP user session and hides the launcher.
 * Keeps the SDK script loaded so SPA re-login can remount safely.
 */
async function endZohoAsapSessionInternal(): Promise<void> {
  stopZohoAsapTokenRefresh();
  document.body.classList.remove(ASAP_AUTHENTICATED_BODY_CLASS);

  if (window.ZohoDeskAsap?.invoke) {
    await invokeLogout();
  }
}

export function endZohoAsapSession(): Promise<void> {
  if (sessionCleanupPromise) {
    return sessionCleanupPromise;
  }

  sessionCleanupPromise = endZohoAsapSessionInternal().finally(() => {
    sessionCleanupPromise = null;
  });
  return sessionCleanupPromise;
}

/**
 * Hard reset — only for tests or broken widget recovery.
 * Removing the SDK script mid-flight corrupts Zoho singletons on SPA re-login.
 */
export async function hardResetZohoAsapWidget(): Promise<void> {
  await endZohoAsapSessionInternal();
  removeZohoAsapDom();
  clearZohoWindowGlobals();
}

/** @deprecated Use endZohoAsapSession — full DOM teardown breaks SDK re-init in SPA. */
export function teardown(): Promise<void> {
  return endZohoAsapSession();
}

/** Call from portal logout handlers — non-blocking; hides widget immediately. */
export function cleanupZohoAsapOnPortalLogout(): void {
  void endZohoAsapSession();
}
